import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch, apiDownload } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';

// ── Types ────────────────────────────────────────────────────────────────────

interface MsgRicevuto {
  id: number;
  ricezione_id: number;
  stato: 'DA_LEGGERE' | 'LETTO';
  lettoAt: string | null;
  oggetto: string;
  corpo: string;
  priorita: string;
  createdAt: string;
  mittente_nome: string;
  mittente_id: number;
  n_allegati: number;
}

interface MsgInviato {
  id: number;
  oggetto: string;
  corpo: string;
  priorita: string;
  createdAt: string;
  n_destinatari: number;
  n_letti: number;
  n_allegati: number;
}

interface Allegato {
  id: number;
  nome: string;
  contentType: string;
  dimensione: number;
  createdAt: string;
}

interface DestRaw {
  id: number; tipo: string;
  utente_nome?: string; sede_nome?: string; societa_nome?: string;
}
interface DestFinale { utenteId: number; nome: string; email: string; }

interface MsgDetail {
  id: number;
  oggetto: string;
  corpo: string;
  priorita: string;
  createdAt: string;
  mittente?: { id: number; nome: string };
  societa?: { id: number; nome: string };
  allegati: Allegato[];
  destinatariRaw?: DestRaw[];
  destinatariFinali?: DestFinale[];
  ricezione?: { stato: string; lettoAt: string | null } | null;
}

const PRIORITA_LABEL: Record<string, string> = { URGENTE: 'Urgente', ALTA: 'Alta', MEDIA: 'Media', BASSA: 'Bassa' };

function PrioritaBadge({ p }: { p: string }) {
  return (
    <span className={`bop-messaggi__priorita-badge bop-messaggi__priorita-badge--${p.toLowerCase()}`}>
      {PRIORITA_LABEL[p] ?? p}
    </span>
  );
}

function formatDate(d: string) {
  const dt = new Date(d);
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
    dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Messaggi() {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canInvio = can('aziendale.messaggi_invio', 'view');

  const [tab, setTab] = useState<'ricevuti' | 'inviati'>('ricevuti');
  const [cerca, setCerca] = useState('');
  const [priFilter, setPriFilter] = useState('');

  const [ricevuti, setRicevuti] = useState<MsgRicevuto[]>([]);
  const [inviati, setInviati] = useState<MsgInviato[]>([]);
  const [loading, setLoading] = useState(false);

  const [selId, setSelId] = useState<number | null>(null);
  const [detail, setDetail] = useState<MsgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [espandiDest, setEspandiDest] = useState(false);
  const [soloNonLetti, setSoloNonLetti] = useState(false);
  const [detailRicezioneStato, setDetailRicezioneStato] = useState<'DA_LEGGERE' | 'LETTO' | null>(null);

  const loadRicevuti = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cerca) params.set('cerca', cerca);
      if (priFilter) params.set('priorita', priFilter);
      const data = await apiFetch<MsgRicevuto[]>(`/api/messaggi/ricevuti?${params}`);
      setRicevuti(data ?? []);
    } catch (_) {} finally { setLoading(false); }
  }, [cerca, priFilter]);

  const loadInviati = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cerca) params.set('cerca', cerca);
      if (priFilter) params.set('priorita', priFilter);
      const data = await apiFetch<MsgInviato[]>(`/api/messaggi/inviati?${params}`);
      setInviati(data ?? []);
    } catch (_) {} finally { setLoading(false); }
  }, [cerca, priFilter]);

  useEffect(() => {
    if (tab === 'ricevuti') loadRicevuti();
    else loadInviati();
  }, [tab, loadRicevuti, loadInviati]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await apiFetch<MsgDetail>(`/api/messaggi/${id}`);
      setDetail(data);
      setDetailRicezioneStato(data.ricezione ? 'LETTO' : null);
      // Aggiorna stato locale a LETTO
      if (tab === 'ricevuti') {
        setRicevuti(prev => prev.map(m => m.id === id ? { ...m, stato: 'LETTO' } : m));
      }
    } catch (_) {} finally { setDetailLoading(false); }
  }, [tab]);

  const handleSelect = (id: number) => {
    setSelId(id);
    setEspandiDest(false);
    loadDetail(id);
  };

  const handleMarkUnread = async () => {
    if (!selId) return;
    try {
      await apiFetch(`/api/messaggi/${selId}/non-letto`, { method: 'PUT' });
      setDetailRicezioneStato('DA_LEGGERE');
      setRicevuti(prev => prev.map(m => m.id === selId ? { ...m, stato: 'DA_LEGGERE' } : m));
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await apiFetch('/api/messaggi/leggi-tutti', { method: 'PUT' });
      setRicevuti(prev => prev.map(m => ({ ...m, stato: 'LETTO' })));
    } catch (_) {}
  };

  const handleDownloadAllegato = async (msgId: number, a: Allegato) => {
    try {
      // @api GET /api/messaggi/:id/allegati/:allegatoId/download -> file stream (attachment)
      await apiDownload(`/api/messaggi/${msgId}/allegati/${a.id}/download`, a.nome || 'allegato');
    } catch (err) {
      console.error('[Messaggi][download allegato]', err);
    }
  };

  const list = tab === 'ricevuti' ? ricevuti : inviati;
  const listFiltrata = (tab === 'ricevuti' && soloNonLetti)
    ? ricevuti.filter(m => m.stato === 'DA_LEGGERE')
    : list;

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Messaggi</h2>
          <p>Comunicazioni interne</p>
        </div>
        <div className="bop-page-header__actions">
          {canInvio && (
            <button className="bop-btn bop-btn-primary" onClick={() => navigate('/aziendale/messaggi/nuovo')}>
              + Nuovo Messaggio
            </button>
          )}
        </div>
      </div>

      <div className="msg-layout">

        {/* ── Lista Messaggi ────────────────────────────────────────────────── */}
        <div className="bop-card bop-messaggi__list-card">

          {/* Tab bar */}
          <div className="bop-messaggi__tab-bar">
            {(['ricevuti', 'inviati'] as const).map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setSelId(null); setDetail(null); }}
                className={`bop-messaggi__tab ${tab === t ? 'bop-messaggi__tab--active' : ''}`}
              >
                {t === 'ricevuti' ? 'Ricevuti' : 'Inviati'}
                {(() => {
                  const cnt = t === 'ricevuti'
                    ? ricevuti.filter(m => m.stato === 'DA_LEGGERE').length
                    : inviati.filter(m => parseInt(String(m.n_letti), 10) === 0 && parseInt(String(m.n_destinatari), 10) > 0).length;
                  return cnt > 0 ? <span className="msg-tab-count">({cnt})</span> : null;
                })()}
              </button>
            ))}
          </div>

          {/* Filtri */}
          <div className="bop-messaggi__filter-bar">
            <input
              className="bop-input bop-messaggi__search-input"
              placeholder="Cerca..."
              value={cerca}
              onChange={e => setCerca(e.target.value)}
            />
            <select
              className="bop-select bop-messaggi__priority-select"
              value={priFilter}
              onChange={e => setPriFilter(e.target.value)}
            >
              <option value="">Tutte</option>
              {['URGENTE', 'ALTA', 'MEDIA', 'BASSA'].map(p => (
                <option key={p} value={p}>{PRIORITA_LABEL[p]}</option>
              ))}
            </select>
          </div>

          {/* Filtro solo non letti */}
          {tab === 'ricevuti' && (
            <div className="bop-messaggi__toolbar-row">
              <label className="msg-nonletti-toggle">
                <input
                  type="checkbox"
                  checked={soloNonLetti}
                  onChange={e => setSoloNonLetti(e.target.checked)}
                />
                Solo non letti
              </label>
            </div>
          )}

          {/* Azioni rapide ricevuti */}
          {tab === 'ricevuti' && ricevuti.some(m => m.stato === 'DA_LEGGERE') && (
            <div className="bop-messaggi__toolbar-row">
              <button
                onClick={handleMarkAllRead}
                className="bop-messaggi__mark-all-btn"
              >
                Segna tutti come letti
              </button>
            </div>
          )}

          {/* Elenco */}
          <div className="msg-list-scroll">
            {loading && <div className="bop-messaggi__list-status">Caricamento...</div>}
            {!loading && listFiltrata.length === 0 && (
              <div className="bop-messaggi__list-status">
                {soloNonLetti && tab === 'ricevuti' ? 'Nessun messaggio non letto' : 'Nessun messaggio'}
              </div>
            )}
            {!loading && listFiltrata.map(m => {
              const isR = tab === 'ricevuti';
              const mr = m as MsgRicevuto;
              const mi = m as MsgInviato;
              const nonLetto = isR && mr.stato === 'DA_LEGGERE';
              return (
                <div
                  key={m.id}
                  onClick={() => handleSelect(m.id)}
                  className={`bop-messaggi__list-item ${selId === m.id ? 'bop-messaggi__list-item--selected' : ''} ${nonLetto ? 'bop-messaggi__list-item--unread' : ''}`}
                >
                  <div className="bop-messaggi__item-header">
                    <span className={`bop-messaggi__item-subject ${nonLetto ? 'bop-messaggi__item-subject--unread' : ''}`}>
                      {m.oggetto}
                    </span>
                    <PrioritaBadge p={m.priorita} />
                  </div>
                  <div className="bop-messaggi__item-meta">
                    <span>{isR ? mr.mittente_nome : `${mi.n_destinatari} dest.`}</span>
                    <span>{formatDate(m.createdAt)}</span>
                  </div>
                  {(m.n_allegati as number) > 0 && (
                    <span className="bop-messaggi__item-allegati">📎 {m.n_allegati} allegat{(m.n_allegati as number) === 1 ? 'o' : 'i'}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Dettaglio Messaggio ────────────────────────────────────────────── */}
        {selId === null ? (
          <div className="msg-placeholder">
            Seleziona un messaggio per visualizzarlo
          </div>
        ) : detailLoading ? (
          <div className="msg-placeholder">
            Caricamento...
          </div>
        ) : detail ? (
          <div className="msg-detail-panel">
            {/* Intestazione */}
            <div className="msg-header">
              <div className="msg-header-title">
                <h3>{detail.oggetto}</h3>
                <PrioritaBadge p={detail.priorita} />
              </div>
              <div className="msg-meta">
                {detail.mittente && <span>Da: <strong>{detail.mittente.nome}</strong></span>}
                <span>{formatDate(detail.createdAt)}</span>
                {detail.societa && <span>Società: {detail.societa.nome}</span>}
              </div>
            </div>

            {/* Azioni header */}
            {tab === 'ricevuti' && detailRicezioneStato === 'LETTO' && (
              <div className="bop-messaggi__action-row">
                <button className="msg-unread-btn" onClick={handleMarkUnread}>
                  Segna come non letto
                </button>
              </div>
            )}

            {/* Corpo */}
            <div className="msg-corpo">
              {detail.corpo}
            </div>

            {/* Allegati */}
            {detail.allegati.length > 0 && (
              <div className="msg-section">
                <p className="msg-section-title">Allegati ({detail.allegati.length})</p>
                <div className="msg-allegati">
                  {detail.allegati.map(a => (
                    <button
                      key={a.id}
                      className="msg-allegato-btn"
                      onClick={() => handleDownloadAllegato(detail.id, a)}
                    >
                      📎 {a.nome}
                      {a.dimensione && <span className="bop-messaggi__allegato-size">({(a.dimensione / 1024).toFixed(0)} KB)</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Destinatari finali (solo per mittente) */}
            {detail.destinatariFinali && detail.destinatariFinali.length > 0 && (
              <div className="msg-section">
                <p className="msg-section-title">Destinatari ({detail.destinatariFinali.length})</p>

                {/* Criteri originali come contesto */}
                {(() => {
                  const criteri = (detail.destinatariRaw ?? [])
                    .filter(d => d.tipo !== 'UTENTE_SINGOLO')
                    .map(d => d.tipo === 'SOCIETA' ? `🏢 ${d.societa_nome}` : `🏠 ${d.sede_nome}`)
                    .filter(Boolean)
                    .join(' · ');
                  return criteri ? (
                    <p className="msg-criteri">Inviato a: {criteri}</p>
                  ) : null;
                })()}

                {/* Tag utenti */}
                <div className="msg-dest-tags">
                  {(espandiDest
                    ? detail.destinatariFinali
                    : detail.destinatariFinali.slice(0, 20)
                  ).map(d => (
                    <span key={d.utenteId} className="msg-dest-tag">{d.nome}</span>
                  ))}
                </div>

                {detail.destinatariFinali.length > 20 && !espandiDest && (
                  <button className="msg-expand-btn" onClick={() => setEspandiDest(true)}>
                    + altri {detail.destinatariFinali.length - 20} destinatari
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
